// pages/Course.tsx
import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { message } from 'antd';
import CourseCard from '../../components/cards/CourseCard';
import { useTranslation } from 'react-i18next';
import { TCourse } from '../../models/courses/course';
import { TAnnonce } from '../../models/annonce/annonce';
import { useCourseApi } from '../../api/courses/courses';
import { useAnnonceApi } from '../../api/annonce/annonces';
import AnnonceBanner from '../../components/AdvertBanner/AnnonceBanner';
import { useNavigate, useParams } from 'react-router-dom';
import './Course.css';

const ALL_SUBJECTS = 'all';

type TSubjectFilter = {
    id: string;
    label: string;
    count: number;
};

const Course: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getCoursesAndProgress } = useCourseApi();
    const { getAnnoncesActives } = useAnnonceApi();
    const [courses, setCourses] = useState<TCourse[]>([]);
    const [annonces, setAnnonces] = useState<TAnnonce[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    // Route historique /subject-courses/:subjectId : on garde le lien profond en
    // pre-selectionnant le parcours dans le filtre.
    const { subjectId } = useParams();
    const [selectedSubject, setSelectedSubject] = useState<string>(
        subjectId ? String(subjectId) : ALL_SUBJECTS
    );

    useEffect(() => {
        setIsLoading(true);

        Promise.all([getCoursesAndProgress(), getAnnoncesActives('tilmicode')])
            .then(([coursesResp, annoncesResp]) => {
                setCourses(coursesResp.data || []);
                setAnnonces(annoncesResp.data || []);
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSelectedSubject(subjectId ? String(subjectId) : ALL_SUBJECTS);
    }, [subjectId]);

    // Un parcours par cours distinct, dans l'ordre renvoye par l'API
    // (subject_order puis course_order).
    const subjectFilters = useMemo<TSubjectFilter[]>(() => {
        const buckets = new Map<string, TSubjectFilter>();
        courses.forEach((course) => {
            const id = String(course.subject_id);
            const existing = buckets.get(id);
            if (existing) {
                existing.count += 1;
                return;
            }
            buckets.set(id, {
                id,
                label: course.subject_title || t('courses.filter.unknownSubject', 'Parcours'),
                count: 1,
            });
        });
        return Array.from(buckets.values());
    }, [courses, t]);

    const visibleCourses = useMemo(() => {
        if (selectedSubject === ALL_SUBJECTS) return courses;
        return courses.filter((course) => String(course.subject_id) === selectedSubject);
    }, [courses, selectedSubject]);

    const stats = useMemo(() => {
        const total = visibleCourses.length;
        const completed = visibleCourses.filter(
            (course) => (course.progression_state ?? 0) >= 100
        ).length;
        const average =
            total === 0
                ? 0
                : Math.round(
                      visibleCourses.reduce(
                          (sum, course) => sum + (course.progression_state ?? 0),
                          0
                      ) / total
                  );

        return {
            total,
            completed,
            average,
        };
    }, [visibleCourses]);

    const selectFilter = (id: string) => {
        setSelectedSubject(id);
        // On sort de la route filtree pour que le filtre reste maitre de l'affichage.
        if (subjectId) {
            navigate('/program', { replace: true });
        }
    };

    return (
        <AppLayout title={t('text.availableCourse')} loading={loading}>
            <div className="courses-page">
                {(annonces || []).map((annonce) => (
                    <AnnonceBanner
                        key={annonce.id}
                        id={annonce.id}
                        title={annonce.title}
                        description={annonce.description || ''}
                        image_url={annonce.image_url || ''}
                        background_color={annonce.background_color || '#FFFFFF'}
                    />
                ))}

                <section className="courses-hero">
                    <div className="courses-hero__content">
                        <span className="courses-hero__eyebrow">
                            {t('courses.hero.eyebrow', 'Course hub')}
                        </span>
                        <h1 className="courses-hero__title">
                            {t('courses.hero.title', 'Choose your next adventure')}
                        </h1>
                        <p className="courses-hero__subtitle">
                            {t(
                                'courses.hero.subtitle',
                                'Each course unlocks fresh missions, challenges, and digital rewards.'
                            )}
                        </p>
                        <div className="courses-hero__stats">
                            <div className="courses-hero__stat">
                                <span className="courses-hero__stat-number">
                                    {stats.total}
                                </span>
                                <span className="courses-hero__stat-label">
                                    {t('courses.stats.total', 'Courses')}
                                </span>
                            </div>
                            <div className="courses-hero__stat">
                                <span className="courses-hero__stat-number">
                                    {stats.completed}
                                </span>
                                <span className="courses-hero__stat-label">
                                    {t('courses.stats.completed', 'Completed')}
                                </span>
                            </div>
                            <div className="courses-hero__stat">
                                <span className="courses-hero__stat-number">
                                    {stats.average}%
                                </span>
                                <span className="courses-hero__stat-label">
                                    {t('courses.stats.average', 'Average progress')}
                                </span>
                            </div>
                        </div>
                    </div>
                </section>

                {subjectFilters.length > 1 && (
                    <section className="courses-filter" aria-label={t('courses.filter.label', 'Filtrer par parcours')}>
                        <span className="courses-filter__label">
                            {t('courses.filter.label', 'Filtrer par parcours')}
                        </span>
                        <div className="courses-filter__chips">
                            <button
                                type="button"
                                className={`courses-filter__chip${selectedSubject === ALL_SUBJECTS ? ' is-active' : ''}`}
                                onClick={() => selectFilter(ALL_SUBJECTS)}
                                aria-pressed={selectedSubject === ALL_SUBJECTS}
                            >
                                {t('courses.filter.all', 'Tous les parcours')}
                                <span className="courses-filter__count">{courses.length}</span>
                            </button>
                            {subjectFilters.map((filter) => (
                                <button
                                    key={filter.id}
                                    type="button"
                                    className={`courses-filter__chip${selectedSubject === filter.id ? ' is-active' : ''}`}
                                    onClick={() => selectFilter(filter.id)}
                                    aria-pressed={selectedSubject === filter.id}
                                    title={filter.label}
                                >
                                    {filter.label}
                                    <span className="courses-filter__count">{filter.count}</span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                <section className="courses-grid">
                    {visibleCourses.map((course, index) => (
                        <CourseCard key={course.id} course={course} index={index} />
                    ))}
                </section>

                {!loading && visibleCourses.length === 0 && (
                    <p className="courses-empty">
                        {t('courses.filter.empty', 'Aucun cours ne correspond à ce parcours.')}
                    </p>
                )}
            </div>
        </AppLayout>
    );
};

export default Course;
